import { useState, useMemo, useEffect } from "react";
import { getProjectById, getProjects } from "../../services/projectService";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useExpenses } from "../../contexts/ExpensesContext";
import { api } from "../../api/client";
import ExpenseForm from "../Invoicing/ExpenseForm";
import ProjectFinancialSummary from "./ProjectFinancialSummary";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Append noon only to a bare date so date-only values ("2026-08-12") don't shift a day across
// timezones; leave a full ISO timestamp (paidAt from the payment webhook) untouched, else
// "…000ZT12:00:00" → Invalid Date / NaN in the timeline math.
function toDate(str) {
  const s = String(str);
  return new Date(s.includes("T") ? s : s + "T12:00:00");
}

function fmtDate(str) {
  if (!str) return "—";
  const d = toDate(str);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function fmtDateShort(str) {
  if (!str) return "";
  const d = toDate(str);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", {
    day: "numeric", month: "short",
  });
}

function calcInvoiceTotal(lineItems = []) {
  const sub = lineItems.reduce((s, r) => s + (r.qty * r.unitPrice), 0);
  const tax = lineItems.reduce((s, r) => s + (r.qty * r.unitPrice * (r.tax || 0) / 100), 0);
  return sub + tax;
}

// ─── Status config (matches InvoicingDashboard) ───────────────────────────────

const STATUS = {
  paid: { bg: "#eaf6ef", text: "#1e6645", border: "#6abf85", label: "Paid" },
  pending: { bg: "#fff8ee", text: "#7a4a10", border: "#FBC490", label: "Pending" },
  overdue: { bg: "#fde8e8", text: "#A12710", border: "#f5a5a5", label: "Overdue" },
  draft: { bg: "#f0f0f0", text: "#888", border: "#ccc", label: "Draft" },
};

function StatusBadge({ status }) {
  const st = STATUS[status] || STATUS.pending;
  return (
    <span style={{
      background: st.bg, color: st.text, border: `1px solid ${st.border}`,
      borderRadius: 8, padding: "0.15rem 0.6rem",
      fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap",
    }}>
      {st.label}
    </span>
  );
}

// ─── Project status badge ─────────────────────────────────────────────────────

function ProjectStatusBadge({ status }) {
  const config = {
    active: { bg: "#eaf6ef", color: "#1e6645", border: "#6abf85" },
    completed: { bg: "#e8f0fe", color: "#1a56db", border: "#93c5fd" },
    paused: { bg: "#fff8ee", color: "#7a4a10", border: "#FBC490" },
    archived: { bg: "#f0f0f0", color: "#888", border: "#ccc" },
  };
  const s = config[status] || config.active;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 6, padding: "0.2rem 0.65rem",
      fontSize: "0.75rem", fontWeight: 700, textTransform: "capitalize",
    }}>
      {status || "active"}
    </span>
  );
}

// ─── SVG Timeline ─────────────────────────────────────────────────────────────

const EVENT_COLOR = {
  start: "#0C2D48",
  end: "#0C2D48",
  invoice: "#FBC490",
  paid: "#6abf85",
};

function ProjectTimeline({ events }) {
  if (events.length === 0) {
    return (
      <p style={{ color: "#bbb", fontSize: "0.85rem", textAlign: "center", padding: "1.5rem 0" }}>
        No dated events to display.
      </p>
    );
  }

  const W = 800;
  const H = 130;
  const PAD_L = 60;
  const PAD_R = 60;
  const USABLE = W - PAD_L - PAD_R;
  const LINE_Y = 65;

  const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
  const timestamps = sorted.map(e => toDate(e.date).getTime());
  const minTs = Math.min(...timestamps);
  const maxTs = Math.max(...timestamps);
  const range = maxTs - minTs || 1;

  const xOf = ts => PAD_L + ((ts - minTs) / range) * USABLE;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", display: "block", overflow: "visible" }}
      aria-label="Project financial timeline"
    >
      {/* Base line */}
      <line
        x1={PAD_L} y1={LINE_Y} x2={W - PAD_R} y2={LINE_Y}
        stroke="#ddd" strokeWidth={2}
      />

      {sorted.map((ev, i) => {
        const ts = toDate(ev.date).getTime();
        const x = xOf(ts);
        const above = i % 2 === 0;
        const dotY = LINE_Y;
        const color = EVENT_COLOR[ev.type] || "#aaa";
        const isEndpoint = ev.type === "start" || ev.type === "end";

        // label positions
        const labelDateY = above ? 44 : 90;
        const labelNameY = above ? 32 : 102;
        const connY1 = above ? dotY - 6 : dotY + 6;
        const connY2 = above ? 50 : 84;

        return (
          <g key={i}>
            {/* Connector */}
            <line
              x1={x} y1={connY1} x2={x} y2={connY2}
              stroke={color} strokeWidth={1.5} strokeDasharray="3,2"
            />

            {/* Dot or diamond */}
            {isEndpoint ? (
              <polygon
                points={`${x},${dotY - 9} ${x + 9},${dotY} ${x},${dotY + 9} ${x - 9},${dotY}`}
                fill={color}
              />
            ) : (
              <circle cx={x} cy={dotY} r={7} fill={color} />
            )}

            {/* Date label */}
            <text
              x={x} y={labelDateY}
              textAnchor="middle"
              fontSize={10}
              fontFamily="Raleway, sans-serif"
              fill="#555"
            >
              {fmtDateShort(ev.date)}
            </text>

            {/* Event name label */}
            <text
              x={x} y={labelNameY}
              textAnchor="middle"
              fontSize={9}
              fontFamily="Raleway, sans-serif"
              fill="#aaa"
            >
              {ev.label.length > 16 ? ev.label.slice(0, 15) + "…" : ev.label}
            </text>
          </g>
        );
      })}

      {/* Legend */}
      {[
        { color: "#0C2D48", label: "Project milestone", shape: "diamond" },
        { color: "#FBC490", label: "Invoice issued", shape: "circle" },
        { color: "#6abf85", label: "Payment received", shape: "circle" },
      ].map((leg, i) => {
        const lx = 20;
        const ly = 14 + i * 16;
        return (
          <g key={leg.label}>
            {leg.shape === "diamond"
              ? <polygon points={`${lx},${ly - 6} ${lx + 6},${ly} ${lx},${ly + 6} ${lx - 6},${ly}`} fill={leg.color} />
              : <circle cx={lx} cy={ly} r={5} fill={leg.color} />
            }
            <text x={lx + 12} y={ly + 4} fontSize={9} fontFamily="Raleway, sans-serif" fill="#888">
              {leg.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Shared table styles ──────────────────────────────────────────────────────

const thStyle = {
  padding: "0.6rem 0.75rem", fontWeight: 700, fontSize: "0.78rem",
  background: "#0C2D48", color: "#fff", textAlign: "left", whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "0.6rem 0.75rem", fontSize: "0.87rem",
  borderBottom: "1px solid #f0f0f0", color: "#333", verticalAlign: "middle",
};

const btnPrimary = {
  padding: "0.5rem 1.1rem", background: "#E4E69B", border: "none",
  borderRadius: 7, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#0C2D48",
};

const btnGhost = {
  padding: "0.4rem 0.85rem", background: "transparent", border: "1px solid #ddd",
  borderRadius: 7, fontWeight: 600, fontSize: "0.83rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#555",
};

const btnSmall = {
  padding: "0.22rem 0.6rem", background: "#f3f3f3", border: "1px solid #ddd",
  borderRadius: 6, fontSize: "0.76rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#333",
};

const sectionTitle = {
  fontFamily: "Poppins, sans-serif", fontSize: "1rem",
  fontWeight: 700, color: "#0C2D48", margin: "0 0 0.75rem",
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * ProjectFinancials
 *
 * Props:
 *   projectId     — numeric project id (required)
 *   onBack        — callback: close this view and return to caller
 *   onNewInvoice  — optional callback(projectId): opens invoice creation form
 *                   with this project pre-selected. If omitted, a fallback
 *                   message is shown.
 */
export default function ProjectFinancials({ projectId, onBack, onNewInvoice }) {
  const { invoices: allInvoices, setInvoices, refresh: refreshInvoices } = useInvoices();
  const { expenses: allExpenses, setExpenses, refresh: refreshExpenses } = useExpenses();
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [toast, setToast] = useState(null);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  const [project, setProject] = useState(() => getProjectById(projectId));

  useEffect(() => {
    let alive = true;
    getProjects()
      .then(() => {
        if (alive) setProject(getProjectById(projectId));
      })
      .catch(() => {
        if (alive) setProject(getProjectById(projectId));
      });

    return () => {
      alive = false;
    };
  }, [projectId]);

  const invoices = useMemo(
    () => allInvoices.filter(i => String(i.project_id) === String(projectId)),
    [allInvoices, projectId],
  );

  const expenses = useMemo(
    () => allExpenses.filter(e => String(e.project_id) === String(projectId)),
    [allExpenses, projectId],
  );

  // ── Financial calculations ────────────────────────────────────────────────

  const financials = useMemo(() => {
    const totalInvoiced = invoices.reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);
    const totalReceived = invoices
      .filter(i => i.status === "paid")
      .reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);
    const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount), 0);
    return { totalInvoiced, totalReceived, totalExpenses };
  }, [invoices, expenses]);

  // ── Timeline events ───────────────────────────────────────────────────────

  const timelineEvents = useMemo(() => {
    const evts = [];
    if (project?.start_date) evts.push({ date: project.start_date, type: "start", label: "Project start" });
    invoices.forEach(inv => {
      if (inv.issueDate) evts.push({ date: inv.issueDate, type: "invoice", label: inv.number });
      if (inv.status === "paid" && inv.paidAt) evts.push({ date: inv.paidAt, type: "paid", label: `Paid: ${inv.number}` });
    });
    if (project?.end_date) evts.push({ date: project.end_date, type: "end", label: "Project end" });
    return evts;
  }, [project, invoices]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleMarkPaid(invoiceId) {
    const paidAt = new Date().toISOString().slice(0, 10);
    setInvoices(prev => prev.map(i => i.id === invoiceId ? { ...i, status: "paid", paidAt } : i));
    showToast("Invoice marked as paid.");
    api(`/api/invoices/${invoiceId}`, { method: 'PUT', body: JSON.stringify({ status: 'paid', paidAt }) })
      .catch(err => { console.error('Failed to mark invoice paid:', err); refreshInvoices(); });
  }

  async function handleSaveExpense(data) {
    const payload = {
      title: data.title,
      amount: data.amount,
      date: data.date,
      category: data.category,
      projectId: projectId,
      contactId: data.contact_id ?? null,
      receiptUrl: data.receipt_url ?? null,
      notes: data.notes ?? '',
      isBillable: data.is_billable ?? false,
      isBilled: data.is_billed ?? false,
      taxRate: data.tax_rate ?? 0,
    };

    if (data.id) {
      setExpenses(prev => prev.map(e => e.id === data.id ? { ...e, ...data } : e));
      showToast("Expense updated.");
      api(`/api/expenses/${data.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        .catch(err => { console.error('Failed to update expense:', err); refreshExpenses(); });
    } else {
      const tempId = Date.now();
      const optimistic = {
        ...data, id: tempId, project_id: projectId, created_at: new Date().toISOString(),
      };
      setExpenses(prev => [optimistic, ...prev]);
      showToast("Expense saved.");
      api('/api/expenses', { method: 'POST', body: JSON.stringify(payload) })
        .then(res => res.json())
        .then(d => {
          if (d.expense) setExpenses(prev => prev.map(e => e.id === tempId ? { ...optimistic, id: Number(d.expense.id) } : e));
        })
        .catch(err => { console.error('Failed to create expense:', err); refreshExpenses(); });
    }
    setShowExpenseForm(false);
    setEditExpense(null);
  }

  function handleDeleteExpense(id) {
    if (!window.confirm("Delete this expense? This cannot be undone.")) return;
    setExpenses(prev => prev.filter(e => e.id !== id));
    showToast("Expense deleted.");
    api(`/api/expenses/${id}`, { method: 'DELETE' })
      .catch(err => { console.error('Failed to delete expense:', err); refreshExpenses(); });
  }

  // ── Guard ─────────────────────────────────────────────────────────────────

  if (!project) {
    return (
      <div style={{ padding: "2rem", color: "#aaa", textAlign: "center" }}>
        Project not found.
        {onBack && <button onClick={onBack} style={{ ...btnGhost, marginLeft: "1rem" }}>Back</button>}
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: "1.25rem", maxWidth: 1100, margin: "0 auto", fontFamily: "Raleway, sans-serif" }}>

      {/* ── HEADER ── */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.5rem",
      }}>
        <div>
          {onBack && (
            <button onClick={onBack} style={{ ...btnGhost, marginBottom: "0.5rem", fontSize: "0.8rem" }}>
              ← Back
            </button>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: "1.4rem", fontFamily: "Poppins, sans-serif", color: "#0C2D48" }}>
              {project.name}
            </h2>
            <ProjectStatusBadge status={project.status} />
          </div>
          {project.client && (
            <div style={{ fontSize: "0.85rem", color: "#666", marginTop: "0.25rem" }}>
              Client: <strong>{project.client}</strong>
            </div>
          )}
          {(project.start_date || project.end_date) && (
            <div style={{ fontSize: "0.8rem", color: "#aaa", marginTop: "0.15rem" }}>
              {project.start_date && `From ${fmtDate(project.start_date)}`}
              {project.start_date && project.end_date && " · "}
              {project.end_date && `To ${fmtDate(project.end_date)}`}
            </div>
          )}
        </div>
      </div>

      {/* ── SUMMARY CARDS ── */}
      <ProjectFinancialSummary
        totalInvoiced={financials.totalInvoiced}
        totalReceived={financials.totalReceived}
        totalExpenses={financials.totalExpenses}
        budget={project.budget || 0}
      />

      {/* ── INVOICE TABLE ── */}
      <div style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
          <h3 style={sectionTitle}>Invoices</h3>
          <button
            onClick={() => onNewInvoice ? onNewInvoice(projectId) : undefined}
            style={btnPrimary}
            title={!onNewInvoice ? "Navigate to the Invoicing section to create a new invoice" : undefined}
          >
            + New invoice for this project
          </button>
        </div>

        {!onNewInvoice && (
          <p style={{ fontSize: "0.8rem", color: "#aaa", marginBottom: "0.75rem" }}>
            To create an invoice for this project, go to <strong>Invoicing</strong> and select this project in the invoice form.
          </p>
        )}

        {invoices.length === 0 ? (
          <p style={{ color: "#bbb", fontSize: "0.88rem", padding: "1.5rem 0" }}>
            No invoices attached to this project yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #35454C", background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead>
                <tr>
                  {["Invoice #", "Date issued", "Due date", "Status", "Amount", "Actions"].map(h => (
                    <th key={h} style={{ ...thStyle, textAlign: h === "Amount" ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const total = calcInvoiceTotal(inv.lineItems);
                  return (
                    <tr
                      key={inv.id}
                      style={{ background: "#fff" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                    >
                      <td style={{ ...tdStyle, fontWeight: 700, color: "#0C2D48" }}>{inv.number}</td>
                      <td style={tdStyle}>{fmtDate(inv.issueDate)}</td>
                      <td style={tdStyle}>{fmtDate(inv.dueDate)}</td>
                      <td style={tdStyle}><StatusBadge status={inv.status} /></td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {fmtMoney(total)}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        {inv.status !== "paid" && (
                          <button
                            onClick={() => handleMarkPaid(inv.id)}
                            style={{
                              ...btnSmall,
                              background: "#eaf6ef", color: "#1e6645",
                              border: "1px solid #6abf85", marginRight: "0.35rem",
                            }}
                          >
                            Mark paid
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f8f9fa" }}>
                  <td colSpan={4} style={{ ...tdStyle, fontWeight: 700, color: "#555", borderBottom: "none" }}>
                    Total
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 900, color: "#0C2D48", whiteSpace: "nowrap", borderBottom: "none" }}>
                    {fmtMoney(financials.totalInvoiced)}
                  </td>
                  <td style={{ ...tdStyle, borderBottom: "none" }} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── EXPENSE TABLE ── */}
      <div style={{ marginBottom: "2rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
          <h3 style={sectionTitle}>Expenses</h3>
          <button
            onClick={() => { setEditExpense(null); setShowExpenseForm(true); }}
            style={btnPrimary}
          >
            + New expense for this project
          </button>
        </div>

        {expenses.length === 0 ? (
          <p style={{ color: "#bbb", fontSize: "0.88rem", padding: "1.5rem 0" }}>
            No expenses attached to this project yet.
          </p>
        ) : (
          <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #35454C", background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr>
                  {["Date", "Title", "Category", "Amount", "Billable", "Billed", "Actions"].map(h => (
                    <th key={h} style={{ ...thStyle, textAlign: h === "Amount" ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => (
                  <tr
                    key={exp.id}
                    style={{ background: "#fff" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                  >
                    <td style={tdStyle}>{fmtDate(exp.date)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#0C2D48", maxWidth: 180 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {exp.title}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {exp.category ? (
                        <span style={{
                          background: "#f0f4f8", color: "#35454C",
                          borderRadius: 6, padding: "0.1rem 0.5rem",
                          fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
                        }}>
                          {exp.category}
                        </span>
                      ) : <span style={{ color: "#ddd" }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap" }}>
                      {fmtMoney(exp.amount)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      {exp.is_billable ? (
                        <span style={{
                          background: "#fff8ee", color: "#7a4a10",
                          border: "1px solid #FBC490",
                          borderRadius: 6, padding: "0.1rem 0.45rem",
                          fontSize: "0.72rem", fontWeight: 700,
                        }}>
                          Billable
                        </span>
                      ) : <span style={{ color: "#ddd" }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>
                      {exp.is_billed ? (
                        <span style={{
                          background: "#eaf6ef", color: "#1e6645",
                          border: "1px solid #6abf85",
                          borderRadius: 6, padding: "0.1rem 0.45rem",
                          fontSize: "0.72rem", fontWeight: 700,
                        }}>
                          Billed
                        </span>
                      ) : <span style={{ color: "#ddd" }}>—</span>}
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <button
                        onClick={() => { setEditExpense(exp); setShowExpenseForm(true); }}
                        style={{ ...btnSmall, marginRight: "0.35rem" }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteExpense(exp.id)}
                        style={{
                          ...btnSmall,
                          background: "#fde8e8", color: "#A12710",
                          border: "1px solid #f5a5a5",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f8f9fa" }}>
                  <td colSpan={3} style={{ ...tdStyle, fontWeight: 700, color: "#555", borderBottom: "none" }}>
                    Total expenses
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 900, color: "#7a4a10", whiteSpace: "nowrap", borderBottom: "none" }}>
                    {fmtMoney(financials.totalExpenses)}
                  </td>
                  <td colSpan={3} style={{ ...tdStyle, borderBottom: "none" }} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ── TIMELINE ── */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h3 style={sectionTitle}>Timeline</h3>
        <div style={{
          background: "#fff", border: "1px solid #35454C",
          borderRadius: 10, padding: "1rem 1.25rem",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}>
          <ProjectTimeline events={timelineEvents} />
        </div>
      </div>

      {/* ── EXPENSE FORM MODAL ── */}
      {showExpenseForm && (
        <ExpenseForm
          expense={editExpense ?? { project_id: projectId }}
          onSave={handleSaveExpense}
          onClose={() => { setShowExpenseForm(false); setEditExpense(null); }}
        />
      )}

      {/* ── TOAST ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: "1.5rem", left: "50%",
          transform: "translateX(-50%)", background: "#0C2D48",
          color: "#fff", padding: "0.7rem 1.4rem", borderRadius: 8,
          fontWeight: 600, fontSize: "0.9rem", zIndex: 2000,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)", whiteSpace: "nowrap",
        }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}
